import { Injectable } from "@angular/core";
import { UserApi } from "@ramudden/data-access/resource/user.api";
import { Rights } from "@ramudden/models/rights";
import { AuthorizationInfo, IOrganization, IUser, IUserSummary, Roles } from "@ramudden/models/user";
import { LocalStorageService } from "@ramudden/services";
import { ModalService } from "@ramudden/ui";
import { Angulartics2GoogleTagManager } from "angulartics2";
import { BehaviorSubject, Observable } from "rxjs";
import { DomainDataService } from "./domain-data.service";
import { nonCoordinatorAccounts } from "./non-coordinator-accounts";

@Injectable({ providedIn: "root" })
export class GlobalEventsService {
    constructor(
        private readonly localStorageService: LocalStorageService,
        private readonly angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
        private readonly domainDataService: DomainDataService,
        private readonly userApi: UserApi,
        private readonly modalService: ModalService,
    ) {
        this.resetRights();
    }

    public isAuthenticated: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
    public isAuthenticated$: Observable<boolean | null> = this.isAuthenticated.asObservable();

    public authorizationInfo: BehaviorSubject<AuthorizationInfo> = new BehaviorSubject<AuthorizationInfo>(null);
    public authorizationInfo$: Observable<AuthorizationInfo> = this.authorizationInfo.asObservable();

    public currentRights: BehaviorSubject<Rights> = new BehaviorSubject<Rights>(null);
    public currentRights$: Observable<Rights> = this.currentRights.asObservable();

    public currentRoles: BehaviorSubject<Roles[]> = new BehaviorSubject<Roles[]>(null);
    public currentRoles$: Observable<Roles[]> = this.currentRoles.asObservable();

    public currentUserName: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    public currentUserName$: Observable<string> = this.currentUserName.asObservable();

    public isAuthorized: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
    public isAuthorized$: Observable<boolean> = this.isAuthorized.asObservable();

    public organization: BehaviorSubject<IOrganization> = new BehaviorSubject<IOrganization>(null);
    public organization$: Observable<IOrganization> = this.organization.asObservable();

    setIsAuthenticated(isAuthenticated: boolean) {
        this.isAuthenticated.next(isAuthenticated);
    }

    getIsAuthenticated(): boolean {
        return this.isAuthenticated.getValue();
    }
    getIsAuthorized(): boolean {
        return this.isAuthorized.getValue();
    }

    setIsAuthorized(value: boolean) {
        this.isAuthorized.next(value);
    }

    getCurrentRights(): Rights {
        return this.currentRights.getValue();
    }

    getCurrentRoles(): Roles[] {
        return this.currentRoles.getValue();
    }

    getAuthorizationInfo(): AuthorizationInfo {
        return this.authorizationInfo.getValue();
    }

    setAuthorizationInfo(user: IUser) {
        const prevAuthorizationInfo = this.authorizationInfo.getValue();
        this.angulartics2GoogleTagManager.setUsername(user ? user.email : null);

        if (user == null) {
            this.resetRights();
        } else if (!prevAuthorizationInfo && user) {
            this.updateLastLoginDate();

            // Apply rights prematurely to be able to instantly show proper UI / load all proper screens in auth.guard
            // If reload fails, it will redirect user anyway
            this.applyRolesAndRights(user);
        }

        const authorizationInfo = new AuthorizationInfo();
        if (user != null) {
            authorizationInfo.isActualDomainAdministrator = this.isActualDomainAdmin(user);
            authorizationInfo.isDomainAdministrator = this.isDomainAdmin(user);
            authorizationInfo.user = user;
            this.authorizationInfo.next(authorizationInfo);
            this.isAuthorized.next(true);
        } else {
            this.authorizationInfo.next(null);
            this.isAuthorized.next(false);
        }
    }

    private async updateLastLoginDate(): Promise<void> {
        return new Promise<void>((resolve) => {
            const onSuccess = () => {
                resolve();
            };

            this.userApi.login$().subscribe(onSuccess);
        });
    }

    //#region Permissions

    private getActualRoles(user: IUser): Roles[] {
        return this.getRoles(user);
    }

    private isDomainAdmin(user: IUser): boolean {
        return this.getRoles(user).contains(Roles.DomainAdministrator);
    }

    private isActualPlanningManager(user: IUser): boolean {
        return this.getActualRoles(user).contains(Roles.PlanningManager);
    }

    private isActualDomainAdmin(user: IUser): boolean {
        return this.getActualRoles(user).contains(Roles.DomainAdministrator);
    }

    private getRoles(user: IUser): Roles[] {
        if (!user) return [];

        const roles: Roles[] = [];

        // Enum value can be 0, which is falsy but not invalid
        roles.push(...user.userRoles); // This pushes every value

        // Add implicit roles to the user based on the roles they have
        // This is the equivalent of `RolesHelper.GetAllRoles` in the backend
        if (roles.contains(Roles.DomainAdministrator)) {
            roles.push(...Object.values(Roles)); // Add all values
        }
        if (roles.contains(Roles.InstallationTechnicalManager)) {
            roles.push(Roles.InstallationManager);
        }
        if (roles.contains(Roles.InstallationManager)) {
            roles.push(Roles.InstallationViewer);
            roles.push(Roles.InstallationRealtimeViewer);
        }

        // Create a Set to remove duplicates, then convert it back to an array
        const result = Array.from(new Set(roles));
        return result;
    }

    private getRights(user: IUser): Rights {
        if (!user) return new Rights();

        // Enum value can be 0, which is falsy but not invalid
        const backendRights = user.rights;
        return new Rights(backendRights);
    }

    getDefaultOrganization(): IOrganization {
        return this.getOrganizations().takeFirstOrDefault();
    }

    getOrganizations(): IOrganization[] {
        const user = this.authorizationInfo.getValue()?.user;
        if (!user) return [];
        return user.userOrganizations.map((x) => x.organization);
    }

    getLinkedOrganizationIds(user: IUser): number[] {
        if (!user) return [];
        return user.linkedOrganizationIds;
    }

    hasAccessToRoleImpersonation(): boolean {
        const user = this.authorizationInfo.getValue()?.user;
        return this.isActualDomainAdmin(user) || this.isActualPlanningManager(user);
    }

    hasMultipleOrganizations(): boolean {
        const user = this.authorizationInfo.getValue()?.user;
        return this.isDomainAdmin(user) || this.getLinkedOrganizationIds(user).length > 1;
    }

    assertAccessToUser(user: IUserSummary): boolean {
        if (!user) return false;

        const currentUser = this.authorizationInfo.getValue()?.user;
        if (currentUser.id == user.id) return true;

        return this.assertAccess(currentUser.linkedOrganizationIds);
    }

    assertAccess(organizationId: number | number[]): boolean {
        if (!organizationId) return false;
        const user = this.authorizationInfo.getValue()?.user;

        const organizationIds = organizationId.toList<number>();
        return !!this.getLinkedOrganizationIds(user).find((x) => organizationIds.contains(x));
    }

    isUser(): boolean {
        const user = this.authorizationInfo.getValue()?.user;
        if (user) {
            return !(this.isActualDomainAdmin(user) || this.isDomainAdmin(user));
        }

        return false;
    }

    /**
     * Whether the logged-in account is a shared login that must never get the MaaS coordinator (werfleiding) user
     * interface. Returns false while the account is still unknown, so callers keep their existing behaviour until
     * the authorization info arrives. See {@link nonCoordinatorAccounts}.
     */
    isNonCoordinatorAccount(): boolean {
        const email = this.authorizationInfo.getValue()?.user?.email;
        if (!email) return false;

        return nonCoordinatorAccounts.includes(email.toLowerCase());
    }

    /**
     * Single source of truth for the MaaS coordinator gating: the role check, overridden by the shared-account
     * exclusion. Used for the setup dialog choice and for the coordinator state kept in `AppService`.
     */
    isMaasCoordinator(): boolean {
        if (this.isNonCoordinatorAccount()) return false;

        return this.getCurrentRoles()?.contains(Roles.MaasCoordinator) ?? false;
    }

    //#endregion Permissions
    //#region [Rights]
    private applyRolesAndRights(user: IUser) {
        const rights = this.getRights(user);
        const roles = this.getRoles(user);
        this.currentRights.next(rights);
        this.currentRoles.next(roles);
    }

    private resetRights() {
        this.currentRights.next(new Rights());
    }
}
